import { useCallback, useEffect } from 'react';

import { Logger } from '../../logger/Logger';
import { gameCommunicationService } from '../../services/gameCommunication/GameCommunicationService';
import type {
  IGameCommunicationProcessorFactory,
  GameCommunicationControllerConnectorProps,
} from '../../services/gameCommunication/types';
import { useCommunicationDelay } from '../../store/apiHooks/useCommunicationDelay';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useGameStateSlice } from '../../store/slices/game/selectors';

import { CoreAutomatedMessagesGameCommunicationProcessor } from './processors/CoreAutomatedMessagesGameCommunicationProcessor';
import { CoreBetGameCommunicationProcessor } from './processors/CoreBetGameCommunicationProcessor';
import { CoreBetsClosedGameCommunicationProcessor } from './processors/CoreBetsClosedGameCommunicationProcessor';
import { CoreBetsGameCommunicationProcessor } from './processors/CoreBetsGameCommunicationProcessor';
import { CoreBetsOpenGameCommunicationProcessor } from './processors/CoreBetsOpenGameCommunicationProcessor';
import { CoreBetValidationErrorGameCommunicationProcessor } from './processors/CoreBetValidationErrorGameCommunicationProcessor';
import { CoreCancelledGameCommunicationProcessor } from './processors/CoreCancelledGameCommunicationProcessor';
import { CoreCommandGameCommunicationProcessor } from './processors/CoreCommandGameCommunicationProcessor';
import { CoreDealerGameCommunicationProcessor } from './processors/CoreDealerGameCommunicationProcessor';
import { CoreEnableSubmitGameCommunicationProcessor } from './processors/CoreEnableSubmitGameCommunicationProcessor';
import { CoreFreeChipsAvailableGameCommunicationProcessor } from './processors/CoreFreeChipsAvailableGameCommunicationProcessor';
import { CoreGameGameCommunicationProcessor } from './processors/CoreGameGameCommunicationProcessor';
import { CoreGameInitGameCommunicationProcessor } from './processors/CoreGameInitGameCommunicationProcessor';
import { CoreMessageGameCommunicationProcessor } from './processors/CoreMessageGameCommunicationProcessor';
import { CorePromotionsBCPGameCommunicationProcessor } from './processors/CorePromotionsBCPGameCommunicationProcessor';
import { CorePromotionsGameCommunicationProcessor } from './processors/CorePromotionsGameCommunicationProcessor';
import { CoreRegulationGameCommunicationProcessor } from './processors/CoreRegulationGameCommunicationProcessor';
import { CoreSessionExpireGameCommunicationProcessor } from './processors/CoreSessionExpireGameCommunicationProcessor';
import { CoreSessionGameCommunicationProcessor } from './processors/CoreSessionGameCommunicationProcessor';
import { CoreStartDealingGameCommunicationProcessor } from './processors/CoreStartDealingGameCommunicationProcessor';
import { CoreSubscribeGameCommunicationProcessor } from './processors/CoreSubscribeGameCommunicationProcessor';
import { CoreSwitchGameCommunicationProcessor } from './processors/CoreSwitchGameCommunicationProcessor';
import { CoreTableClosedGameCommunicationProcessor } from './processors/CoreTableClosedGameCommunicationProcessor';
import { CoreTableCloseNotifyGameCommunicationProcessor } from './processors/CoreTableCloseNotifyGameCommunicationProcessor';
import { CoreTerminateSessionProcessor } from './processors/CoreTerminateSessionProcessor';
import { CoreThirdPartyErrorGameCommunicationProcessor } from './processors/CoreThirdPartyErrorGameCommunicationProcessor';
import { CoreTimerGameCommunicationProcessor } from './processors/CoreTimerGameCommunicationProcessor';
import { CoreWinGameCommunicationProcessor } from './processors/CoreWinGameCommunicationProcessor';
import { CoreWinnersGameCommunicationProcessor } from './processors/CoreWinnersGameCommunicationProcessor';

const gameCommunicationProcessors: IGameCommunicationProcessorFactory[] = [
  CoreBetGameCommunicationProcessor,
  CoreBetValidationErrorGameCommunicationProcessor,
  CoreBetsClosedGameCommunicationProcessor,
  CoreBetsOpenGameCommunicationProcessor,
  CoreBetsGameCommunicationProcessor,
  CoreCancelledGameCommunicationProcessor,
  CoreCommandGameCommunicationProcessor,
  CoreDealerGameCommunicationProcessor,
  CoreSessionExpireGameCommunicationProcessor,
  CoreEnableSubmitGameCommunicationProcessor,
  CoreGameInitGameCommunicationProcessor,
  CoreGameGameCommunicationProcessor,
  CoreMessageGameCommunicationProcessor,
  CorePromotionsGameCommunicationProcessor,
  CorePromotionsBCPGameCommunicationProcessor,
  CoreRegulationGameCommunicationProcessor,
  CoreStartDealingGameCommunicationProcessor,
  CoreSubscribeGameCommunicationProcessor,
  CoreSwitchGameCommunicationProcessor,
  CoreTableCloseNotifyGameCommunicationProcessor,
  CoreTableClosedGameCommunicationProcessor,
  CoreThirdPartyErrorGameCommunicationProcessor,
  CoreTimerGameCommunicationProcessor,
  CoreWinGameCommunicationProcessor,
  CoreWinnersGameCommunicationProcessor,
  CoreSessionGameCommunicationProcessor,
  CoreFreeChipsAvailableGameCommunicationProcessor,
  CoreTerminateSessionProcessor,
  CoreAutomatedMessagesGameCommunicationProcessor,
];

const gameCommunicationSenders: any[] = [];

export const GameCommunicationControllerConnector = ({
  gameStore,
  config,
}: GameCommunicationControllerConnectorProps): null => {
  const JSESSIONID = useConfigSlice.JSESSIONID();
  const tableId = useConfigSlice.tableId();
  const tableConfig = useConfigSlice.tableConfig();
  const isGamePaused = useGameStateSlice.getIsGamePaused();
  const gameChannel = useGameStateSlice.gameChannel();
  const gameCommunicationDelay = useCommunicationDelay();
  const isGameAbandoned = useGameStateSlice.getIsGameAbandoned();
  const isUserInfoReady = useGameStateSlice.userInfoReady();
  const launchSource = useConfigSlice.launchSource();

  useEffect(() => {
    gameCommunicationService.comEventBus.addEventListener('earlySend', (sender, event) =>
      Logger.logException(
        new Error(
          `Game Communication Controller received message while being not ready from ${sender.name} with details: ${event.detail}`
        )
      )
    );

    gameCommunicationService.comEventBus.addEventListener('serverSwitch', () =>
      Logger.logEvent('serverSwitchExecuted')
    );
  }, []);

  // Flag which prevents unnecessary connection in case of unavailable game table
  const shouldHaltCommunication = useCallback((): boolean => {
    const isTableClose = tableConfig?.tableClosed;

    return Boolean(isTableClose);
  }, [tableConfig]);

  useEffect(() => {
    // Setting up Processors
    const processors = gameCommunicationProcessors.concat(config?.processors ?? []);
    processors.forEach((processor) => gameCommunicationService.setProcessor(processor, gameStore));

    // Setting up Senders
    const senders = gameCommunicationSenders.concat(config?.senders ?? []);
    // Redundant. Needed only due to invalid senders
    if (gameStore) {
      gameCommunicationService.setStore(gameStore);
    }
    senders.forEach((sender) => gameCommunicationService.setSender(sender));

    if (config?.connectionObserver) {
      gameCommunicationService.setConnectionObserver(config.connectionObserver);
    }

    gameCommunicationService.setCredentialStrategy(config?.credentialStrategy);
  }, [config, gameStore]);

  useEffect(() => {
    gameCommunicationService.stopStart(isGamePaused);
  }, [isGamePaused]);

  useEffect(() => {
    if (isGameAbandoned) {
      gameCommunicationService.killCommunication();
    }
  }, [isGameAbandoned]);

  useEffect(() => {
    if (gameChannel) {
      gameCommunicationService.setInitialized();
      gameCommunicationService.communicationStrategy.setGameChannel(gameChannel);
    }
  }, [gameChannel]);

  useEffect(() => {
    if (gameCommunicationDelay) {
      gameCommunicationService.setServerMessageDelay(gameCommunicationDelay);
    }
  }, [gameCommunicationDelay]);

  useEffect(() => {
    if (!shouldHaltCommunication() && tableConfig && JSESSIONID && tableId && isUserInfoReady) {
      gameCommunicationService.setCredentials({
        url: tableConfig.wsAddress,
        jsessionId: JSESSIONID,
        tableId,
        launchSource: launchSource ?? undefined,
      });
      gameCommunicationService.init();
    }
  }, [shouldHaltCommunication, tableConfig, tableId, JSESSIONID, isUserInfoReady, launchSource]);

  return null;
};
